import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowSmRightIcon } from "@heroicons/react/outline";
import { FormikProps } from "formik";
import RadioButtonsField from "../../components/Form/RadioButtonsField";
import TextInputField from "../../components/Form/TextInputField";
import { HTTPFormMeta } from "../../components/HTTPForm/Meta";
import InboxActionButtonPreview from "./InboxActionButtonPreview";
import * as meta from "./InboxActionMeta";
import { INBOX_ACTION_TYPES } from "./InboxActionMeta";

interface InboxActionFormBaseFieldsProps {
  formik: FormikProps<any>;
  setCurrentStep: (s: string) => void;
}

export default function InboxActionFormBaseFields({
  formik,
  setCurrentStep,
}: InboxActionFormBaseFieldsProps) {
  const { t } = useTranslation();
  const [previousActionType, setPreviousActionType] = useState(
    formik.values.action_type
  );

  useEffect(() => {
    const currentActionType = formik.values.action_type;

    if (currentActionType !== previousActionType) {
      if (previousActionType === "HTTP") {
        formik.setFieldValue("request_method", undefined);
        formik.setFieldValue("request_url", undefined);
        formik.setFieldValue("request_headers", undefined);
        formik.setFieldValue("request_params", undefined);
        formik.setFieldValue("request_body", undefined);
        formik.setFieldValue("body_type", undefined);
        formik.setFieldValue("body_params", undefined);
      } else if (previousActionType === "FLOW") {
        formik.setFieldValue("related_flow", undefined);
      }

      if (currentActionType === "HTTP") {
        const httpInitialValues = HTTPFormMeta.getInitialValues();
        Object.entries(httpInitialValues).forEach(([key, value]) => {
          formik.setFieldValue(key, value);
        });
      } else if (currentActionType === "FLOW") {
        formik.setFieldValue("related_flow", "");
      }

      setPreviousActionType(currentActionType);
    }
  }, [formik.values.action_type, previousActionType]);

  return (
    <div>
      <div className="row mt-10 text-center">
        <h6 className="text-tiny uppercase tracking-wider text-gray-500">
          {t("inbox-actions:builder.step-1-of-2", "Step 1 of 2")}
        </h6>
        <h1 className="mt-5 mb-1 text-2xl font-bold leading-7 text-gray-900">
          {t(
            "inbox-actions:builder.what-will-your-button-do",
            "What will your button do?"
          )}
        </h1>
        <p className="mb-5 text-sm text-gray-500">
          {t(
            "inbox-actions:builder.create-and-personalize-button",
            "Create and personalize the button that will trigger your Inbox Action."
          )}
        </p>
      </div>

      <div className="mt-10 grid grid-cols-3 grid-rows-1 gap-6 sm:grid-cols-1">
        <div className="col-span-2">
          <h6 className="text-tiny uppercase tracking-wider text-gray-500">
            {t("inbox-actions:builder.button-setup", "Button setup")}
          </h6>

          <div className="col-span-2 mt-5 md:mt-0">
            <div className="shadow sm:overflow-hidden sm:rounded-md">
              <div className="space-y-6 bg-white px-4 py-5 sm:p-6">
                <TextInputField
                  placeholder={t(meta.InboxAction.FIELDS.name.placeholder)}
                  label={t(meta.InboxAction.FIELDS.name.label)}
                  name={meta.InboxAction.FIELDS.name.key}
                  help={t(meta.InboxAction.FIELDS.name.help)}
                  optional={meta.InboxAction.FIELDS.name.optional}
                  type="text"
                />
                <RadioButtonsField
                  label={t("inbox-actions:builder.action-type", "Action Type")}
                  name="action_type"
                  options={INBOX_ACTION_TYPES.map((actionType) => ({
                    value: actionType.value,
                    label: t(actionType.label),
                    icon: actionType.icon,
                    disabled: false,
                  }))}
                />
                <TextInputField
                  placeholder={t(
                    meta.InboxAction.FIELDS.button_label.placeholder
                  )}
                  label={t(meta.InboxAction.FIELDS.button_label.label)}
                  name={meta.InboxAction.FIELDS.button_label.key}
                  help={t(meta.InboxAction.FIELDS.button_label.help)}
                  optional={meta.InboxAction.FIELDS.button_label.optional}
                  type="text"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <h6 className="text-tiny uppercase tracking-wider text-gray-500">
            {t("inbox-actions:builder.button-preview", "Button preview")}
          </h6>
          <div className="mt-2 overflow-hidden">
            <InboxActionButtonPreview
              buttonLabel={
                formik.values[meta.InboxAction.FIELDS.button_label.key]
              }
              buttonType={
                formik.values[meta.InboxAction.FIELDS.action_type.key]
              }
            />
          </div>
        </div>
      </div>

      <div className="mx-auto my-14 max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <hr className="my-5" />
          <div className="nav grid grid-cols-3 grid-rows-1 items-center gap-4">
            <div className=""></div>
            <div className="col text-center">
              <h6 className="mb-0 text-tiny uppercase tracking-wider text-gray-500">
                {t("inbox-actions:builder.step-1-of-2", "Step 1 of 2")}
              </h6>
            </div>
            <div className="justify-self-end">
              <button
                className="inline-flex items-center rounded-md border border-gray-300 bg-blue-500 px-4 py-3 text-sm font-medium leading-4 text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-blue-500"
                type="button"
                onClick={(_) => setCurrentStep("http")}
              >
                {t("inbox-actions:builder.action-details", "Action Details")}
                <ArrowSmRightIcon className="ml-2 h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
